import type { LicenseKeyResponse } from '@shared/types/LicenseKey';
import { useConfigStore } from '@/Stores/config';
import { useContextStore } from '@/Stores/context';
import { useBookmarksStore } from '@/Stores/bookmarks';
import { getAvailableLanguages } from '@/Language/availableLanguages';

const VALID_TEMPLATES = ['wlan', 'tv', 'terminal', 'default', 'streaming'] as const;

const DEFAULT_LANGUAGE = 'de';

let contextStore: ReturnType<typeof useContextStore> | null = null;

// contextStore is assigned once, at the top of ensureLoadedIfComplete() below, before any of
// this module's other functions (all reachable only from there) run -- this getter documents
// and enforces that invariant instead of silently trusting it via a non-null assertion.
function getContextStore(): ReturnType<typeof useContextStore> {
  if (!contextStore) {
    throw new Error('contextStore accessed before ensureLoadedIfComplete() initialized it');
  }
  return contextStore;
}

export type ContextField = 'language' | 'experience' | 'template';

export type LoadErrorCode =
  'INCOMPLETE_CONTEXT' | 'INVALID_TEMPLATE' | 'LANG_NOT_SUPPORTED' | 'CONFIG_ERROR' | 'UNKNOWN';

export interface LoadResult {
  valid: boolean;
  error?: string;
  code?: LoadErrorCode;
  missing?: ContextField[];
}

async function getNewLicenseKey(): Promise<void> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/licenseKey`);
  if (!response.ok) throw new Error(`Error fetching License Key! Status: ${response.status}`);
  const respJson = (await response.json()) as Partial<LicenseKeyResponse>;
  const data = { key: respJson.token, expirationDate: respJson.expirationDate };
  if (!data.key || !data.expirationDate) throw new Error('Fehlende oder ungültige Lizenzdaten');
  const licenseData = { key: data.key, expires: data.expirationDate };
  localStorage.setItem('pages-license-key', JSON.stringify(licenseData));
  getContextStore().licenseKey = data.key;
}

async function getLicenseKey(): Promise<void> {
  const raw = localStorage.getItem('pages-license-key');
  if (!raw) {
    await getNewLicenseKey();
    return;
  }
  const { key, expires } = JSON.parse(raw) as { key: string; expires: number };
  if (!key || !Number.isFinite(expires)) {
    await getNewLicenseKey();
    return;
  }
  if (Date.now() >= expires * 1000) {
    await getNewLicenseKey();
    return;
  }
  getContextStore().licenseKey = key;
}

export const CONTEXT_FIELDS: readonly ContextField[] = ['language', 'experience', 'template'];

export function getMissingContextFields(
  context: Pick<ReturnType<typeof useContextStore>, ContextField>,
): ContextField[] {
  return CONTEXT_FIELDS.filter((field) => !context[field]);
}

// Error pages keep the experience colors; a failure falls back to the default colors.
async function loadExperienceColors(): Promise<void> {
  const configStore = useConfigStore();
  if (!configStore.config && !configStore.isLoading) {
    await configStore.fetchConfig();
  }
}

/**
 * Loads configuration + menu schema if needed and validates language + template.
 */
export async function loadConfiguration(): Promise<LoadResult> {
  const { template, language } = getContextStore();

  try {
    if (!(VALID_TEMPLATES as readonly string[]).includes(template)) {
      await loadExperienceColors();
      return { valid: false, code: 'INVALID_TEMPLATE', error: 'Invalid template' };
    }

    const configStore = useConfigStore();
    // loadExperienceColors() may have loaded the config without the menu.
    if (!configStore.isLoading) {
      await Promise.all([
        configStore.config ? undefined : configStore.fetchConfig(),
        configStore.menu ? undefined : configStore.fetchMenu(),
      ]);
    }
    if (configStore.error) {
      return { valid: false, code: 'CONFIG_ERROR', error: configStore.error };
    }

    getContextStore().applyCapabilitiesFromConfig(configStore.config);

    const languages = getAvailableLanguages(configStore.config?.portal_settings?.languages);
    if (!languages.includes(language) && language !== DEFAULT_LANGUAGE) {
      return { valid: false, code: 'LANG_NOT_SUPPORTED', error: 'Language not supported' };
    }

    // get temporary license key and save it in local storage and context store
    await getLicenseKey();

    useBookmarksStore().load();

    return { valid: true };
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : undefined;
    return { valid: false, code: 'UNKNOWN', error: message || 'Unknown error' };
  }
}

/**
 * Only attempts loading if all contextual parts exist; otherwise returns an error result.
 */
export async function ensureLoadedIfComplete(): Promise<LoadResult> {
  contextStore = useContextStore();

  const missing = getMissingContextFields(contextStore);

  if (missing.length > 0) {
    if (!missing.includes('experience')) {
      await loadExperienceColors();
    }

    return {
      valid: false,
      code: 'INCOMPLETE_CONTEXT',
      error: 'Incomplete context',
      missing,
    };
  }

  return loadConfiguration();
}
